import {Col, Form, Input, InputNumber, Row, Select} from "antd";
import {memo, useEffect} from "react";
import {useIntl} from "react-intl";
import getStandingsAdjustmentFieldsWithTranslations from "../../config/standingsAdjustmentFields";
import {SelectStage} from "~components/SelectStage/SelectStage";
import {SelectPool} from "~components/SelectPool/SelectPool";

const {TextArea} = Input;

const StandingsAdjustmentsForm = ({standingsAdjustments, handleSetFormRef, entityList}) => {
  const {formatMessage} = useIntl();
  const [form] = Form.useForm();

  const initValues = {
    applyToAllStandings: false,
    adjustmentField: "position",
    adjustmentGroup: "OVERALL",
    ...standingsAdjustments,
  };

  useEffect(() => {
    handleSetFormRef(form);
  }, []);

  const ADJUSTMENT_GROUPS = ["OVERALL", "IN_CONFERENCE", "OUT_CONFERENCE", "IN_DIVISION", "OUT_DIVISION"];
  const ADJUSTMENT_TYPES = [
    {
      value: "SET",
      text: formatMessage({
        id: "set.value",
        defaultMessage: "Set Value",
      }),
    },
    {
      value: "ADD_MINUS",
      text: formatMessage({
        id: "add.minus",
        defaultMessage: "Add/Substract Value",
      }),
    },
  ];

  const ADJUSTMENT_FIELDS = getStandingsAdjustmentFieldsWithTranslations(formatMessage);

  return (
    <Form
      form={form}
      labelCol={{
        span: 12,
      }}
      colon={false}
      labelWrap={true}
      initialValues={initValues}
    >
      <Row>
        <Col span={13}>
          <Form.Item
            labelCol={{
              span: 6,
            }}
            name={"entityId"}
            label={formatMessage({
              id: "team",
              defaultMessage: "Team",
            })}
            tooltip={formatMessage({
              id: "form.help.standing.adjustments.team",
              defaultMessage: "The team that the standings adjustements data is for",
            })}
            rules={[{required: true}]}
          >
            <Select disabled={standingsAdjustments?.standingAdjustmentId}>
              {entityList?.map((entity) => (
                <Select.Option value={entity.entityId} key={entity.entityId}>
                  {entity.nameFullLocal} {entity.gender ? " (" + entity.gender.charAt(0) + ")" : ""}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={11}>
          <Form.Item
            labelCol={{
              span: 18,
            }}
            name={"applyToAllStandings"}
            label={formatMessage({
              id: "apply.to.all.standings",
              defaultMessage: "Apply to all standings",
            })}
            tooltip={formatMessage({
              id: "form.help.standing.adjustments.apply.to.all",
              defaultMessage: "Apply to all generated standing types?",
            })}
          >
            <Select>
              <Select.Option value={""}>-</Select.Option>
              <Select.Option value={true}>Yes</Select.Option>
              <Select.Option value={false}>No</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={13}>
          <Form.Item
            labelCol={{
              span: 12,
            }}
            name={"adjustmentGroup"}
            label={formatMessage({
              id: "adjustment.group",
              defaultMessage: "Adjustment Group",
            })}
            tooltip={formatMessage({
              id: "form.help.standing.adjustments.group",
              defaultMessage: "The group for the standings adjustment",
            })}
          >
            <Select>
              {ADJUSTMENT_GROUPS?.map((adjustmentGroup) => (
                <Select.Option value={adjustmentGroup} key={adjustmentGroup}>
                  {adjustmentGroup}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={11}>
          <Form.Item
            labelCol={{
              span: 12,
            }}
            name={"adjustmentType"}
            label={formatMessage({
              id: "adjustment.type",
              defaultMessage: "Adjustment Type",
            })}
            tooltip={formatMessage({
              id: "form.help.standing.adjustments.type",
              defaultMessage: "The type of adjustment",
            })}
            rules={[{required: true}]}
          >
            <Select>
              <Select.Option value={""}>-</Select.Option>
              {ADJUSTMENT_TYPES?.map((adjustmentType) => (
                <Select.Option value={adjustmentType.value} key={adjustmentType.value}>
                  {adjustmentType.text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={13}>
          <Form.Item
            labelCol={{
              span: 10,
            }}
            name={"adjustmentField"}
            label={formatMessage({
              id: "adjustment.field",
              defaultMessage: "Adjustment Field",
            })}
            tooltip={formatMessage({
              id: "form.help.standing.adjustments.field",
              defaultMessage: "The field of adjustment",
            })}
            rules={[{required: true}]}
          >
            <Select>
              {ADJUSTMENT_FIELDS?.map((adjustmentField) => (
                <Select.Option value={adjustmentField.value} key={adjustmentField.value}>
                  {adjustmentField.text}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={11}>
          <Form.Item
            labelCol={{span: 14}}
            name={["adjustmentValue"]}
            label={formatMessage({
              id: "adjustment.value",
              defaultMessage: "Adjustment Value",
            })}
            tooltip={formatMessage({
              id: "form.help.standing.adjustments.value",
              defaultMessage: "The value of adjustment",
            })}
            rules={[{required: true}]}
          >
            <InputNumber style={{width: "100%"}}/>
          </Form.Item>
        </Col>
        <Col span={13}>
          <Form.Item
            name={["groupingKey"]}
            label={formatMessage({
              id: "adjustment.groupingKey",
            })}
            tooltip={formatMessage({
              id: "form.help.standing.adjustments.groupingKey",
            })}
          >
            <Input/>
          </Form.Item>
        </Col>
        <Col span={11}>
          <Form.Item
            name={"stageCode"}
            label={formatMessage({
              id: "stage",
            })}
            tooltip={formatMessage({
              id: "form.help.standing.adjustments.stage",
            })}
          >
            <SelectStage onChange={(value) => form.setFieldsValue({stageCode: value.value})}/>
          </Form.Item>
        </Col>
        <Col span={13}>
          <Form.Item
            name={"poolCode"}
            label={formatMessage({
              id: "pool",
              defaultMessage: "Pool",
            })}
            tooltip={formatMessage({
              id: "form.help.standing.adjustments.pool",
              defaultMessage: "The pool for the standings adjustment",
            })}
          >
            <SelectPool onChange={(value) => form.setFieldsValue({poolCode: value.value})}/>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            labelCol={{span: 8}}
            name={["reasonType"]}
            label={formatMessage({
              id: "standings.adjustment.reason",
              defaultMessage: "Adjustment Reason",
            })}
            tooltip={formatMessage({
              id: "form.help.standing.adjustments.reason",
              defaultMessage: "The reason for adding a standings adjustment",
            })}
            rules={[{required: true}]}
          >
            <TextArea rows={4}/>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            labelCol={{span: 8}}
            name={["reasonDescription"]}
            label={formatMessage({
              id: "standings.adjustment.reason.description",
              defaultMessage: "Adjustment Description",
            })}
            tooltip={formatMessage({
              id: "form.help.standing.adjustments.description",
              defaultMessage: "The description for adding a standings adjustment",
            })}
          >
            <TextArea rows={4}/>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default memo(StandingsAdjustmentsForm);
